/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import { StarIcon } from "@heroicons/react/24/outline";
import LocaleContext from "components/i18n/LocaleContext";
import getStarLabel from "components/rating/getStarLabel";
import TextTooltip from "components/shared/TextTooltip";
import classNames from "components/ui/classNames";
import useSelfie from "lib/hooks/useSelfie";
import { useContext, useState } from "react";

interface IRatingStarsProps {
  rating: number;
  isSubmittingRating: boolean;
  onSubmitRating: (rating: number) => void;
}

function RatingStars({ rating, isSubmittingRating, onSubmitRating }: IRatingStarsProps) {
  const selfie = useSelfie();
  const locale = useContext(LocaleContext);
  const [isRating, setIsRating] = useState(false);
  const [ratingForStar, setRatingForStar] = useState<number | null>(null);
  const viewerCanRate = selfie && selfie.isPremium;

  return (
    <div
      className={classNames("flex items-center justify-center space-x-2 xl:inline-flex xl:justify-start", {
        "animate-pulse": isSubmittingRating,
      })}
      onMouseOver={() => {
        if (viewerCanRate) {
          setIsRating(true);
        }
      }}
      onMouseOut={() => {
        if (viewerCanRate) {
          setIsRating(false);
        }
      }}
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const label = getStarLabel(star, locale);
        return (
          <TextTooltip
            key={star}
            id={`tooltips/RatingStars/${star}`}
            placement="top"
            content={label}
            style={{
              maxWidth: 260,
              textAlign: "center",
              fontWeight: 700,
            }}
          >
            {({ accessibility, bind, ref }) => {
              let isStarFilled = false;
              if (isRating) {
                isStarFilled = ratingForStar === null ? false : ratingForStar >= star;
              } else {
                isStarFilled = rating === 0 ? false : rating >= star;
              }
              return (
                <button
                  type="button"
                  disabled={!selfie || !selfie.isPremium}
                  {...accessibility}
                  {...bind}
                  ref={ref}
                  aria-label={label}
                  onMouseOver={() => {
                    if (viewerCanRate) {
                      setRatingForStar(star);
                    }
                  }}
                  onMouseOut={() => {
                    if (viewerCanRate) {
                      setRatingForStar(null);
                    }
                  }}
                  onClick={() => {
                    if (viewerCanRate) {
                      onSubmitRating(star);
                    }
                  }}
                >
                  <StarIcon
                    strokeWidth={1.5}
                    className={classNames("h-7 w-7 text-slate-400", {
                      "fill-amber-500 text-amber-500": isStarFilled,
                    })}
                  />
                </button>
              );
            }}
          </TextTooltip>
        );
      })}
    </div>
  );
}

export default RatingStars;
